import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../services/apiService';
import { eventService, EventType } from '../../services/eventService';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Button,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { MapContainer, TileLayer, Marker, Popup, useMap, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for missing marker icons in Leaflet with direct URLs instead of imports
// (This avoids webpack image import issues)
const iconUrl = 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png';
const shadowUrl = 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// ---------------------------------------------------------------------------
// Icônes Leaflet color-markers — palette suspension (plan §0 tâche 15)
//   gris   = inactif
//   vert   = actif + toutes alertes acquittées
//   orange = actif + alertes non acquittées  (ex-rouge "alertIcon" renommé)
//   rouge  = suspension active
//   bleu   = suspension programmée + toutes ack
//   violet = suspension programmée + alertes non ack
// ---------------------------------------------------------------------------

const BASE_URL = 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img';
const ICON_OPTS = {
  shadowUrl,
  iconSize:    [25, 41] as [number, number],
  iconAnchor:  [12, 41] as [number, number],
  popupAnchor: [1, -34] as [number, number],
  shadowSize:  [41, 41] as [number, number],
};

const activeIcon         = new L.Icon({ iconUrl: `${BASE_URL}/marker-icon-2x-green.png`,  ...ICON_OPTS });
const inactiveIcon       = new L.Icon({ iconUrl: `${BASE_URL}/marker-icon-2x-grey.png`,   ...ICON_OPTS });
const unackIcon          = new L.Icon({ iconUrl: `${BASE_URL}/marker-icon-2x-orange.png`, ...ICON_OPTS });
const suspendedIcon      = new L.Icon({ iconUrl: `${BASE_URL}/marker-icon-2x-red.png`,    ...ICON_OPTS });
const scheduledOkIcon    = new L.Icon({ iconUrl: `${BASE_URL}/marker-icon-2x-blue.png`,   ...ICON_OPTS });
const scheduledUnackIcon = new L.Icon({ iconUrl: `${BASE_URL}/marker-icon-2x-violet.png`, ...ICON_OPTS });

// Component to handle map operations like recentering
interface MapOperationsProps {
  position?: [number, number];
  zoom?: number;
  highlightSensor?: number;
}

const MapOperations: React.FC<MapOperationsProps> = ({ position, zoom = 12, highlightSensor }) => {
  const map = useMap();
  
  useEffect(() => {
    if (position) {
      map.setView(position, zoom);
    }
  }, [map, position, zoom]);
  
  return null;
};

// Sensor interface
interface Sensor {
  id: number;
  name: string;
  serial_number: string;
  imei: string;
  ccid: string;
  location_description: string;
  axis?: string;
  direction?: string;
  pr?: string;
  is_active: boolean;
  last_seen?: string;
  alert_count: number;
  last_alert_date?: string;
  suspension_status?: 'active' | 'scheduled' | null;
  location?: {
    type: string;
    coordinates: [number, number];
  };
}

const SensorMap: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const mapRef = useRef<L.Map | null>(null);
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [center, setCenter] = useState<[number, number]>([48.8566, 2.3522]); // Default to Paris
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [showAlertsOnly, setShowAlertsOnly] = useState(false);
  const [sensorsWithUnacknowledgedAlerts, setSensorsWithUnacknowledgedAlerts] = useState<number[]>([]);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);

  // Get highlight sensor ID from location state
  const highlightSensor = location.state?.highlightSensor as number | undefined;

  // Fetch unacknowledged alerts
  const fetchUnacknowledgedAlerts = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await apiClient.get('/alerts/recent/unacknowledged');
      const unacknowledgedAlerts = response.data;
      
      // Extract unique sensor IDs from unacknowledged alerts
      // Using Array.filter approach to avoid Set spreading TypeScript error
      const sensorIds = unacknowledgedAlerts
        .map((alert: any) => alert.sensor_id)
        .filter((id: number, index: number, self: number[]) => 
          self.indexOf(id) === index
        );
      setSensorsWithUnacknowledgedAlerts(sensorIds);
    } catch (err) {
      console.error('Error fetching unacknowledged alerts:', err);
      // We don't set an error here to avoid disrupting the map if this fails
    }
  }, [isAuthenticated]);

  // Fetch sensor data with locations
  const fetchSensors = useCallback(async () => {
    if (!isAuthenticated) {
      setError(t('sensorMap.errors.loginRequired'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Use apiClient with correct endpoint (trailing slash)
      const response = await apiClient.get('/sensors/');
      // Transform sensors to include location object for compatibility
      const transformedSensors = response.data.items.map((s: any) => {
        // Only include sensors with both latitude and longitude
        if (s.latitude !== null && s.longitude !== null) {
          return {
            ...s,
            location: {
              type: 'Point',
              coordinates: [s.longitude, s.latitude]
            }
          };
        }
        return s;
      });
      
      // Filter out sensors without location data
      const sensorsWithLocation = transformedSensors.filter((s: Sensor) => s.location);
      
      setSensors(sensorsWithLocation);
      
      // Also fetch unacknowledged alerts
      fetchUnacknowledgedAlerts();
      
      // Calculate map center based on sensors
      if (sensorsWithLocation.length > 0) {
        // If we have a highlighted sensor, center on that
        if (highlightSensor) {
          const highlightedSensor = sensorsWithLocation.find((s: Sensor) => s.id === highlightSensor);
          if (highlightedSensor?.location) {
            const [lng, lat] = highlightedSensor.location.coordinates;
            setCenter([lat, lng]);
          }
        } 
        // Otherwise center based on all sensors
        else {
          const latSum = sensorsWithLocation.reduce((sum: number, s: Sensor) => 
            sum + s.location!.coordinates[1], 0);
          const lngSum = sensorsWithLocation.reduce((sum: number, s: Sensor) => 
            sum + s.location!.coordinates[0], 0);
          
          setCenter([
            latSum / sensorsWithLocation.length,
            lngSum / sensorsWithLocation.length
          ]);
        }
      }
      
    } catch (err: any) {
      console.error('Error fetching sensors:', err);
      // Ensure error is a string, not an object
      const errorDetail = err.response?.data?.detail;
      setError(
        typeof errorDetail === 'object' ? JSON.stringify(errorDetail) : 
        errorDetail || t('sensorMap.errors.failedToLoad')
      );
    } finally {
      setLoading(false);
    }
  }, [highlightSensor, isAuthenticated, fetchUnacknowledgedAlerts, t]);

  // Initial data load
  useEffect(() => {
    fetchSensors();
  }, [fetchSensors]);

  // Subscribe to new alert events
  useEffect(() => {
    if (isAuthenticated) {
      // Set up event listener for new alerts
      const unsubscribe = eventService.subscribe(EventType.NEW_ALERT, () => {
        // Refresh map data when a new alert is received
        fetchSensors();
        fetchUnacknowledgedAlerts();
      });
      
      // Clean up subscription when component unmounts
      return () => {
        unsubscribe();
      };
    }
  }, [isAuthenticated, fetchSensors, fetchUnacknowledgedAlerts]);

  // Navigate to sensor detail
  const handleSensorClick = (sensorId: number) => {
    navigate(`/sensors/${sensorId}`);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchSensors();
    fetchUnacknowledgedAlerts();
  };

  // Handle locating user
  const handleLocateUser = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCenter([latitude, longitude]);
          setUserLocation([latitude, longitude]);
          if (mapRef.current) {
            mapRef.current.setView([latitude, longitude], 14);
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          setError(t('sensorMap.errors.locationAccessDenied'));
        }
      );
    } else {
      setError(t('sensorMap.errors.geolocationNotSupported'));
    }
  };

  // Filter sensors based on switches
  const filteredSensors = sensors.filter(sensor => {
    if (showActiveOnly && !sensor.is_active) return false;
    if (showAlertsOnly && !(sensor.alert_count > 0)) return false;
    return true;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('sensorMap.title')}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showActiveOnly}
                onChange={(e) => setShowActiveOnly(e.target.checked)}
                color="primary"
              />
            }
            label={t('sensorMap.activeOnly')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showAlertsOnly}
                onChange={(e) => setShowAlertsOnly(e.target.checked)}
                color="warning"
              />
            }
            label={t('sensorMap.withAlertsOnly')}
          />
          <Tooltip title={t('sensorMap.refresh')}>
            <IconButton onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('sensorMap.findMyLocation')}>
            <IconButton onClick={handleLocateUser}>
              <MyLocationIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              {t('sensorMap.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Paper sx={{ height: 'calc(100vh - 180px)', position: 'relative', overflow: 'hidden' }}>
        {loading && sensors.length === 0 ? (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1000,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <MapContainer
              center={center}
              zoom={8}
              style={{ height: '100%', width: '100%' }}
              ref={mapRef}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {/* User location marker */}
              {userLocation && (
                <CircleMarker
                  center={userLocation}
                  radius={10}
                  pathOptions={{
                    color: '#1976d2',
                    fillColor: '#1976d2',
                    fillOpacity: 0.6,
                    weight: 2,
                  }}
                >
                  <Popup>
                    {t('sensorMap.youAreHere')}
                  </Popup>
                </CircleMarker>
              )}

              {filteredSensors.map((sensor) => {
                if (!sensor.location) return null;
                
                // Convert GeoJSON coordinates (longitude, latitude) to Leaflet coordinates (latitude, longitude)
                const [longitude, latitude] = sensor.location.coordinates;
                const position: [number, number] = [latitude, longitude];
                
                // ----------------------------------------------------------
                // Logique de choix d'icône (plan §0 tâche 15) :
                // 1. inactif                              → gris
                // 2. suspension active                   → rouge
                // 3. suspension programmée + toutes ack  → bleu
                // 4. suspension programmée + unack        → violet
                // 5. alertes non ack (pas de suspension)  → orange
                // 6. actif + toutes ack                  → vert
                // ----------------------------------------------------------
                const hasUnack = sensorsWithUnacknowledgedAlerts.includes(sensor.id);
                let markerIcon = inactiveIcon;

                if (sensor.is_active) {
                  if (sensor.suspension_status === 'active') {
                    markerIcon = suspendedIcon;
                  } else if (sensor.suspension_status === 'scheduled') {
                    markerIcon = hasUnack ? scheduledUnackIcon : scheduledOkIcon;
                  } else if (hasUnack) {
                    markerIcon = unackIcon;
                  } else {
                    markerIcon = activeIcon;
                  }
                }
                
                // Highlight the selected sensor
                const isHighlighted = sensor.id === highlightSensor;
                
                return (
                  <Marker 
                    key={sensor.id} 
                    position={position}
                    icon={markerIcon}
                    zIndexOffset={isHighlighted ? 1000 : 0}
                  >
                    <Popup>
                      <Box sx={{ p: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {sensor.name}
                        </Typography>
                        <Typography variant="body2">
                          {sensor.location_description || t('sensorMap.noLocationDescription')}
                        </Typography>
                        {sensor.axis && (
                          <Typography variant="body2">
                            {t('sensor.fields.axis')} : {sensor.axis}
                          </Typography>
                        )}
                        {sensor.direction && (
                          <Typography variant="body2">
                            {t('sensor.fields.direction')} : {sensor.direction}
                          </Typography>
                        )}
                        {sensor.pr && (
                          <Typography variant="body2">
                            {t('sensor.fields.pr')} : {sensor.pr}
                          </Typography>
                        )}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {t('sensor.fields.serialNumber')}: {sensor.serial_number}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          IMEI: {sensor.imei}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Chip 
                            label={sensor.is_active ? t('common.active') : t('common.inactive')}
                            color={sensor.is_active ? 'success' : 'error'} 
                            size="small" 
                          />
                          <Button 
                            size="small" 
                            onClick={() => handleSensorClick(sensor.id)}
                          >
                            {t('sensors.view')}
                          </Button>
                        </Box>
                      </Box>
                    </Popup>
                  </Marker>
                );
              })}
              
              <MapOperations 
                position={center} 
                zoom={highlightSensor ? 14 : 8} 
                highlightSensor={highlightSensor}
              />
            </MapContainer>
            
            {/* Map Legend */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                backgroundColor: 'white',
                borderRadius: 1,
                p: 1,
                boxShadow: 1,
                zIndex: 1000,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                {t('sensorMap.legend')}
              </Typography>
              {[
                { color: 'grey',   key: 'inactiveSensor' },
                { color: 'green',  key: 'activeAcknowledgedSensor' },
                { color: 'orange', key: 'sensorWithUnacknowledgedAlerts' },
                { color: 'red',    key: 'sensorSuspended' },
                { color: 'blue',   key: 'sensorScheduledOk' },
                { color: 'violet', key: 'sensorScheduledUnack' },
              ].map(({ color, key }) => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Box sx={{ width: 14, height: 14, backgroundColor: color, borderRadius: '50%', mr: 1, flexShrink: 0 }} />
                  <Typography variant="body2">{t(`sensorMap.${key}`)}</Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Paper>
      
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {filteredSensors.length} {t('sensorMap.sensorsDisplayed')}
        </Typography>
        <Button 
          variant="outlined" 
          size="small" 
          onClick={() => navigate('/sensors')}
        >
          {t('sensorMap.viewSensorList')}
        </Button>
      </Box>
    </Box>
  );
};

export default SensorMap;